import axios from 'axios';

import localAxios from '../localAxios';
import { imageUploadUrl } from './imageUrls';
import {
  InvalidImageSizeError,
  UnSupportedImageTypeError,
  isSupportedImageType,
  isValidImageSize,
} from './utils';

export type OnUploadProgress = (progressEvent: {
  loaded: number;
  total: number;
}) => void;

interface UploadImageParams {
  files: File[];
  onUploadProgress?: (progressEvent: ProgressEvent) => void;
}

export async function uploadImage({
  files,
  onUploadProgress,
}: UploadImageParams): Promise<string> {
  const file = files[0];

  if (!isSupportedImageType({ file })) {
    throw new UnSupportedImageTypeError();
  }

  if (!isValidImageSize({ file })) {
    throw new InvalidImageSizeError();
  }

  const { uploadUrl, imageUrl } = await getSignedUrl({
    size: file.size,
    type: file.type,
  });

  await axios.put(uploadUrl, file, {
    withCredentials: false,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: onUploadProgress,
  });

  return imageUrl;
}

type GetSignedURLResponse = {
  imageName: string;
  uploadUrl: string;
  imageUrl: string;
};

type GetSignedURLParams = {
  size: number;
  type: string;
};

/*
  This function returns a signed url for the image upload.
  The signed url is for the image upload and not for the image itself.
  @param size - The size of the image.
  @param type - The type of the image.
  @returns The signed url for the image upload.
*/
async function getSignedUrl({
  size,
  type,
}: GetSignedURLParams): Promise<GetSignedURLResponse> {
  const url = imageUploadUrl();
  const response = await localAxios.post<GetSignedURLResponse>(url.href, {
    content_length: size,
    content_type: type,
  });
  return response.data;
}
